import { Controller } from "@hotwired/stimulus"
import i18n from "../javascripts/util/i18n"

// Client-side staged editor for the module questionnaire "Build" tab. Edits,
// reordering, deletes, creates (from a condition) and option changes all live in
// the DOM — nothing persists until "Save all" serializes every card to JSON and
// posts it to QuestionsController#bulk_update. Conditions move between the left
// panel and question cards live; the `conditions` value is the full registry so
// freed conditions can reappear.
export default class extends Controller {
  static targets = [
    "card", "list", "payload", "form", "count", "emptyState", "dirtyHint", "restore", "sortControls",
    "conditionItem", "conditionList", "typeDialog", "addOptionDialog", "addOptionList", "deleteDialog", "restoreDialog", "chipTemplate"
  ]
  static values = { newCardUrl: String, conditions: Array, insertLabel: String }

  connect() {
    // Typing in a card's title or description is a change too; it didn't mark the page unsaved (QA).
    this.onFieldEdit = (event) => { if (event.target.closest?.("[data-question-field]")) this.markDirty() }
    this.element.addEventListener("input", this.onFieldEdit)
    this.element.addEventListener("change", this.onFieldEdit)
    this.pendingConditionId = null
    this.activeCard = null
    this.draggedCondition = null
    this.conditionsById = new Map((this.conditionsValue || []).map((c) => [String(c.id), c]))
    this.refreshState()
  }

  // ── Reorder ──────────────────────────────────────────────────────────────
  moveUp(event) { const c = this.cardOf(event); const p = this.sibling(c, -1); if (p) { c.parentNode.insertBefore(c, p); this.markDirty() } }
  moveDown(event) { const c = this.cardOf(event); const n = this.sibling(c, 1); if (n) { c.parentNode.insertBefore(n, c); this.markDirty() } }

  // ── Delete (staged, confirmed) — return its conditions to the panel ──────
  requestDelete(event) {
    this.pendingDeleteCard = this.cardOf(event)
    this.open(this.deleteDialogTarget)
  }

  confirmDelete() {
    const card = this.pendingDeleteCard
    this.pendingDeleteCard = null
    this.close(this.deleteDialogTarget)
    if (!card) return
    this.cardConditionIds(card).forEach((id) => this.freeCondition(id))
    card.dataset.destroyed = "true"
    card.classList.add("hidden")
    this.markDirty()
    this.refreshState()
  }

  closeDelete() { this.close(this.deleteDialogTarget) }

  // ── Create from a condition ──────────────────────────────────────────────
  startCreate(event) {
    this.pendingConditionId = event.currentTarget.dataset.conditionId
    this.pendingInsertRef = null
    this.open(this.typeDialogTarget)
  }

  async selectType(event) {
    const type = event.currentTarget.dataset.questionType
    const conditionId = this.pendingConditionId
    this.close(this.typeDialogTarget)
    if (!conditionId || !type || !this.hasNewCardUrlValue) return

    const url = new URL(this.newCardUrlValue, window.location.origin)
    url.searchParams.set("condition_id", conditionId)
    url.searchParams.set("question_type", type)
    const response = await fetch(url, { headers: { Accept: "text/html" }, credentials: "same-origin" })
    if (!response.ok) return

    const html = await response.text()
    const ref = this.pendingInsertRef
    if (ref && ref.parentNode === this.listTarget && ref.dataset.destroyed !== "true") {
      ref.insertAdjacentHTML("beforebegin", html)
    } else {
      this.listTarget.insertAdjacentHTML("beforeend", html)
    }
    this.pendingInsertRef = null
    this.removeConditionItem(conditionId)
    this.pendingConditionId = null
    this.markDirty()
    this.refreshState()
  }

  // ── Options via the modal ────────────────────────────────────────────────
  openAddOption(event) {
    this.activeCard = this.cardOf(event)
    this.renderAddOptionList()
    this.open(this.addOptionDialogTarget)
  }

  confirmAddOption() {
    const options = this.optionsAreaOf(this.activeCard)
    if (options) {
      this.addOptionListTarget.querySelectorAll("input[type=checkbox]:checked").forEach((cb) => {
        this.addOptionChip(options, cb.value, cb.dataset.title)
      })
      this.markDirty()
      this.refreshState()
    }
    this.close(this.addOptionDialogTarget)
  }

  removeOption(event) {
    const chip = event.target.closest("[data-option-condition-id]")
    if (!chip) return
    this.freeCondition(chip.dataset.optionConditionId)
    chip.remove()
    this.markDirty()
    this.refreshState()
  }

  // ── Options via drag & drop ──────────────────────────────────────────────
  conditionDragStart(event) {
    const item = event.currentTarget
    this.draggedCondition = { id: item.dataset.conditionId, title: item.dataset.conditionTitle, key: item.dataset.key }
    event.dataTransfer.effectAllowed = "copy"
    event.dataTransfer.setData("text/plain", item.dataset.conditionId)
    item.classList.add("dnd-dragging")
    this.dragging = true
    // Defer .dnd-active by a frame: making the drag source pointer-events:none
    // synchronously inside dragstart cancels the drag in Chrome.
    requestAnimationFrame(() => { if (this.dragging) this.element.classList.add("dnd-active") })
  }

  conditionDragEnd() { this.endDrag() }

  // Reset all drag state. Called by dragend, but also directly by drop handlers
  // that remove the drag source from the DOM — in that case the browser never
  // fires dragend, so the dnd-active dimming would otherwise stick.
  endDrag() {
    this.dragging = false
    this.draggedCondition = null
    this.clearInsertIndicator()
    this.element.classList.remove("dnd-active")
    this.element.querySelectorAll(".dnd-dragging").forEach((el) => el.classList.remove("dnd-dragging"))
    this.element.querySelectorAll(".dnd-over").forEach((el) => el.classList.remove("dnd-over"))
  }

  optionDragOver(event) {
    if (!this.draggedCondition) return
    event.stopPropagation()
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    event.currentTarget.classList.add("dnd-over")
  }

  optionDragLeave(event) {
    event.currentTarget.classList.remove("dnd-over")
  }

  optionDrop(event) {
    event.stopPropagation()
    event.preventDefault()
    const condition = this.draggedCondition
    const options = event.currentTarget.querySelector('[data-questionnaire-editor-target="options"]')
    if (condition && options && !options.querySelector(`[data-option-condition-id="${condition.id}"]`)) {
      this.addOptionChip(options, condition.id, condition.title)
      this.markDirty()
      this.refreshState()
    }
    this.endDrag()
  }

  // ── Create via drag & drop onto the dropzone ─────────────────────────────
  createDragOver(event) {
    if (!this.draggedCondition) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    event.currentTarget.classList.add("dnd-over")
  }

  createDragLeave(event) {
    event.currentTarget.classList.remove("dnd-over")
  }

  createDrop(event) {
    event.preventDefault()
    const condition = this.draggedCondition
    if (condition) {
      this.pendingConditionId = condition.id
      this.pendingInsertRef = null
      this.open(this.typeDialogTarget)
    }
    this.endDrag()
  }

  // ── Insert-between: drag a condition over the question list ───────────────
  // Shows an insertion bar in the gap under the cursor so a new question can be
  // dropped anywhere in the order, not just at the bottom zone. Option-drop
  // areas stopPropagation, so hovering them never triggers an insert.
  listDragOver(event) {
    if (!this.draggedCondition) return
    // Only in the gaps between questions — never over a card (each card owns its
    // own option drop zone, so showing the insert zone there is a conflict).
    if (event.target.closest('[data-questionnaire-editor-target="card"]')) {
      this.clearInsertIndicator()
      return
    }
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    this.showInsertIndicator(this.insertRefFromPoint(event.clientY))
  }

  listDragLeave(event) {
    if (!this.listTarget.contains(event.relatedTarget)) this.clearInsertIndicator()
  }

  listDrop(event) {
    if (!this.draggedCondition) return
    if (event.target.closest('[data-questionnaire-editor-target="card"]')) return
    event.preventDefault()
    this.pendingConditionId = this.draggedCondition.id
    this.pendingInsertRef = this.currentInsertRef
    this.clearInsertIndicator()
    this.open(this.typeDialogTarget)
    this.endDrag()
  }

  // First non-deleted card whose vertical midpoint is below the cursor → insert
  // before it; past the last card → append (null).
  insertRefFromPoint(clientY) {
    const cards = this.cardTargets.filter((c) => c.dataset.destroyed !== "true")
    for (const card of cards) {
      const rect = card.getBoundingClientRect()
      if (clientY < rect.top + rect.height / 2) return card
    }
    return null
  }

  showInsertIndicator(ref) {
    this.currentInsertRef = ref
    const bar = this.ensureInsertIndicator()
    if (ref) {
      if (bar.nextSibling !== ref) this.listTarget.insertBefore(bar, ref)
    } else if (bar !== this.listTarget.lastElementChild) {
      this.listTarget.appendChild(bar)
    }
  }

  ensureInsertIndicator() {
    if (!this.insertIndicator) {
      const zone = document.createElement("div")
      // Same look as the bottom "drop to create" zone, highlighted as the active target.
      zone.className = "flex items-center justify-center rounded-xl border-2 border-dashed border-brand-500 bg-brand-50 px-4 py-6 text-center text-sm text-brand-600 dark:border-brand-500 dark:bg-brand-500/10 dark:text-brand-300"
      zone.textContent = this.hasInsertLabelValue ? this.insertLabelValue : i18n.t("js.questionnaire_editor.drop_to_create")
      zone.setAttribute("data-qz-insert-indicator", "true")
      this.insertIndicator = zone
    }
    return this.insertIndicator
  }

  clearInsertIndicator() {
    this.insertIndicator?.remove()
    this.currentInsertRef = null
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  // A radio or checkbox question with every option removed can't be answered; the server refuses it, and its refusal
  // redraws the saved questionnaire, so the staged work would go with it. Said here first, nothing lost (QA).
  save() {
    const empty = this.liveCards().filter((card) => card.dataset.questionType !== "boolean_group" && !card.querySelector("[data-option-condition-id]"))
    if (empty.length) {
      empty.forEach((card) => card.classList.add("ring-2", "ring-error-500"))
      empty[0].scrollIntoView({ block: "center", behavior: "smooth" })
      window.boToast?.(this.element.dataset.noOptionsMessage || "A radio or checkbox question needs at least one option.", "alert")
      return
    }
    this.cardTargets.forEach((card) => card.classList.remove("ring-2", "ring-error-500"))
    this.payloadTarget.value = JSON.stringify(this.serialize())
    this.formTarget.requestSubmit()
  }

  serialize() {
    return this.cardTargets.map((card) => ({
      id: card.dataset.questionId || "",
      condition_id: card.dataset.conditionId || "",
      question_type: card.dataset.questionType,
      title: this.field(card, "title"),
      description: this.field(card, "description"),
      none_of_the_above: this.field(card, "none_of_the_above"),
      none_of_the_above_title: this.field(card, "none_of_the_above_title"),
      condition_ids: Array.from(card.querySelectorAll("[data-option-condition-id]")).map((el) => el.dataset.optionConditionId),
      _destroy: card.dataset.destroyed === "true" ? "1" : "0"
    }))
  }

  field(card, name) {
    const el = card.querySelector(`[data-question-field="${name}"]`)
    if (!el) return ""
    return el.type === "checkbox" ? (el.checked ? "1" : "0") : el.value
  }

  // ── Condition panel sync ─────────────────────────────────────────────────
  refreshState() {
    const live = this.liveCards().length
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.toggle("hidden", live > 0)
    if (this.hasCountTarget) {
      const d = this.countTarget.dataset
      const label = live === 1 ? d.labelOne : d.labelOther
      this.countTarget.textContent = `${live} ${label || ""}`.trim()
    }
    this.sortControlsTargets.forEach((el) => el.classList.toggle("hidden", live < 2))
  }

  removeConditionItem(conditionId) {
    const item = this.conditionItemTargets.find((el) => el.dataset.conditionId === String(conditionId))
    if (item) item.remove()
  }

  // Re-insert a freed condition into the panel (from the full registry).
  freeCondition(conditionId) {
    const id = String(conditionId)
    if (!id || !this.hasConditionListTarget) return
    if (this.conditionItemTargets.some((el) => el.dataset.conditionId === id)) return
    const condition = this.conditionsById.get(id)
    if (condition) this.conditionListTarget.insertAdjacentHTML("beforeend", this.conditionItemTemplate(condition))
  }

  renderAddOptionList() {
    const available = this.conditionItemTargets
    if (available.length === 0) {
      this.addOptionListTarget.innerHTML = `<p class="text-sm text-gray-500 dark:text-gray-400">${this.escape(this.addOptionListTarget.dataset.emptyLabel)}</p>`
      return
    }
    this.addOptionListTarget.innerHTML = available.map((item) => `
      <label class="flex items-center gap-3 rounded-md px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-800">
        <input type="checkbox" value="${item.dataset.conditionId}" data-title="${this.escape(item.dataset.conditionTitle)}" class="bo-checkbox bo-checkbox-brand" />
        <span class="flex-1 text-sm text-gray-700 dark:text-gray-200"><span class="font-medium">${this.escape(item.dataset.conditionTitle)}</span></span>
      </label>`).join("")
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  addOptionChip(optionsArea, conditionId, title) {
    const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true)
    chip.dataset.optionConditionId = conditionId
    chip.querySelector("[data-chip-label]").textContent = title
    optionsArea.appendChild(chip)
    this.removeConditionItem(conditionId)
  }

  cardConditionIds(card) {
    const ids = new Set()
    if (card.dataset.conditionId) ids.add(card.dataset.conditionId)
    card.querySelectorAll("[data-option-condition-id]").forEach((el) => ids.add(el.dataset.optionConditionId))
    return ids
  }

  optionsAreaOf(card) { return card ? card.querySelector('[data-questionnaire-editor-target="options"]') : null }
  liveCards() { return this.cardTargets.filter((c) => c.dataset.destroyed !== "true") }
  cardOf(event) { return event.target.closest('[data-questionnaire-editor-target="card"]') }
  sibling(card, dir) { const live = this.liveCards(); const i = live.indexOf(card); return live[i + dir] || null }
  // Also tells the page's unsaved-changes guard, so leaving asks first instead of dropping staged questions (QA).
  markDirty() {
    if (this.hasDirtyHintTarget) this.dirtyHintTarget.classList.remove("hidden")
    if (this.hasRestoreTarget) this.restoreTarget.classList.remove("hidden")
    this.element.dispatchEvent(new CustomEvent("unsaved-changes:dirty", { bubbles: true }))
  }

  // ── Restore (discard all staged changes, confirmed) ──────────────────────
  requestRestore() { this.open(this.restoreDialogTarget) }
  // Restoring discards the staged changes on purpose, so the guard doesn't ask about them.
  confirmRestore() {
    this.element.dispatchEvent(new CustomEvent("unsaved-changes:clean", { bubbles: true }))
    window.location.reload()
  }
  closeRestore() { this.close(this.restoreDialogTarget) }

  conditionItemTemplate(condition) {
    const title = this.escape(condition.title)
    const key = this.escape(condition.key)
    return `<li class="cursor-grab select-none" data-questionnaire-editor-target="conditionItem" data-condition-item data-condition-id="${condition.id}" data-condition-title="${title}" data-title="${title}" data-key="${key}" draggable="true" data-action="dragstart->questionnaire-editor#conditionDragStart dragend->questionnaire-editor#conditionDragEnd" x-show="matchesCondition($el.dataset.title, $el.dataset.key)">
      <div class="block rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-700 transition hover:border-brand-200 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-white/5">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium">${title}</span>
          <button type="button" data-action="questionnaire-editor#startCreate" data-condition-id="${condition.id}" class="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"><i class="fa-solid fa-plus text-xs" aria-hidden="true"></i></button>
        </div>
        <div class="mt-1 text-xs text-gray-500 dark:text-gray-400">${key}</div>
      </div>
    </li>`
  }

  escape(value) {
    return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  open(dialog) { if (dialog) dialog.showModal ? dialog.showModal() : dialog.setAttribute("open", "open") }
  close(dialog) { if (dialog) dialog.close ? dialog.close() : dialog.removeAttribute("open") }
  closeType() { this.close(this.typeDialogTarget) }
  closeAddOption() { this.close(this.addOptionDialogTarget) }
}
